import { useCallback, useEffect, useState } from "react";
import { Button, Empty, Input, Popconfirm, Spin, message } from "antd";
import {
  fetchVaSignUpSettings,
  saveVaSignUpSettings,
} from "../../../service/settings";

const MAX_NAMES = 60;

const toLines = (names) => (names || []).join("\n");
const fromLines = (text) =>
  text
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);

/**
 * Admin settings tab for the VA sign-up documents.
 *
 * Item 19b of VA Form 21-22a names the firm's administrative staff VA may
 * also share the veteran's records with. The list prints on every new
 * sign-up packet; saving here changes packets sent from then on, never ones
 * already sent (Brendan, 2026-11-05: "something we can edit whenever we want").
 */
const VaSignUpSettings = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetchVaSignUpSettings();
      setData(res?.data || null);
      setDraft(toLines(res?.data?.va_19b_names));
    } catch (err) {
      console.error("Failed to load VA sign-up settings:", err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (names) => {
    setSaving(true);
    try {
      const res = await saveVaSignUpSettings(names);
      setData(res?.data || null);
      setDraft(toLines(res?.data?.va_19b_names));
      message.success(
        names.length ? "Item 19b list saved." : "Item 19b is back to the original list."
      );
    } catch (err) {
      message.error(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Couldn't save the list — try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (loadError && !data) {
    return (
      <Empty
        description="Couldn't load the VA sign-up settings. Refresh the page or try again later."
        style={{ padding: 48 }}
      />
    );
  }

  const names = fromLines(draft);
  const changed = toLines(names) !== toLines(data?.va_19b_names);
  const tooMany = names.length > MAX_NAMES;
  const hasBar = names.some((n) => n.includes("|"));

  return (
    <div data-testid="va-sign-up-settings" style={{ maxWidth: 720 }}>
      <h3 style={{ marginBottom: 4 }}>VA Form 21-22a, item 19b</h3>
      <p style={{ color: "#6b7280", marginBottom: 16 }}>
        The firm&apos;s administrative staff VA may also share the
        veteran&apos;s records with. One name per line, written as it should
        print (for example &ldquo;Jane Doe, Esq.&rdquo;). Saving changes the
        form on sign-up packets sent from now on; packets already sent keep
        the list they were sent with.
      </p>
      <Input.TextArea
        data-testid="va-19b-names"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoSize={{ minRows: 10, maxRows: 30 }}
        disabled={saving}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 8,
          gap: 16,
        }}
      >
        <span
          data-testid="va-19b-status"
          style={{ color: tooMany || hasBar ? "#b91c1c" : "#6b7280", fontSize: 12 }}
        >
          {names.length} {names.length === 1 ? "name" : "names"}
          {tooMany ? ` — at most ${MAX_NAMES} fit` : ""}
          {hasBar ? " — put each name on its own line instead of using |" : ""}
          {!tooMany && !hasBar
            ? data?.va_19b_is_default
              ? " · the original list from the Formstack form"
              : data?.updated_by
              ? ` · last changed by ${data.updated_by}${
                  data.updated_at
                    ? ` on ${new Date(data.updated_at).toLocaleDateString()}`
                    : ""
                }`
              : ""
            : ""}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          {!data?.va_19b_is_default && (
            <Popconfirm
              title="Go back to the original list from the Formstack form?"
              onConfirm={() => save([])}
              okText="Go back"
            >
              <Button data-testid="va-19b-reset" disabled={saving}>
                Use the original list
              </Button>
            </Popconfirm>
          )}
          <Button
            type="primary"
            data-testid="va-19b-save"
            onClick={() => save(names)}
            loading={saving}
            disabled={!changed || !names.length || tooMany || hasBar}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VaSignUpSettings;
